import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import {
  Accordion,
  AccordionItem,
  Button,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  CollapseAllIcon,
  CountChip,
  DateInput,
  ExpandAllIcon,
  Hint,
  PencilIcon,
  PrinterIcon,
  SaveIcon,
  Select,
  Textarea,
  TicketIcon,
  UserPlusIcon,
  VesselSelect,
  PortSelect,
  WarningIcon,
  XIcon,
} from '../../components/ui';
import Badge from '../../components/ui/Badge';
import ConfirmationDialog from '../../components/ui/ConfirmationDialog';
import { Modal, ModalContent, ModalFooter, ModalHeader, ModalTitle } from '../../components/ui/Modal';
import { formatDate } from '../../config/appConfig';
import { useTheme } from '../../contexts/ThemeContext';
import { usePersistedListFilters } from '../../hooks/usePersistedListFilters';
import { getListStateStorageKey } from '../../lib/listStatePersistence';
import { cn } from '../../lib/utils';
import { fetcher } from '../../services/fetcher';
import api from '../../services/api';
import {
  advanceCrewReplacement,
  addCrewReplacementCandidate,
  addCrewReplacementCandidateForTour,
  completeCrewReplacement,
  createCrewTravelFromReplacement,
  removeCrewReplacementCandidate,
  seedCrewReplacements,
  selectCrewReplacementCandidate,
  updateCrewReplacement,
} from '../../services/mutations';
import CrewReportSearchHeader, { CrewReportFilterField } from './CrewReportSearchHeader';
import { CrewDataTablePrompt, WITHIN_DAYS_OPTIONS, useCrewReportOpen } from './reportShared';
import { ReferenceSidebarSplitter } from '../parameters/reference-workspaces/ReferenceSidebarChrome';
import { useResizablePanel } from '../parameters/reference-workspaces/useResizablePanel';
import {
  buildBoardLines,
  crewName,
  detectConflicts,
  filterBoardLines,
  getAssignWarnings,
  needsRelief,
  replacementPlanPrintPath,
  type BoardLine,
} from './replacementPlannerUtils';
import ReplacementAuditFlow from './ReplacementAuditFlow';
import type {
  CrewReplacementAuditResponse,
  CrewReplacementCandidateRow,
  CrewReplacementResponse,
  CrewReplacementRow,
  CrewReplacementStatus,
  CrewVesselListResponse,
  CrewVesselListRow,
  ReliefBoardResponse,
  ReliefBoardRow,
} from './types';

const ROUTE = '/crew/replacement-planner';
const STORAGE_KEY = getListStateStorageKey(ROUTE);
const DND_MIME = 'application/x-atlas-relief-crew';

const IconBtn: React.FC<{
  title: string;
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
  tone?: 'primary' | 'muted' | 'danger';
  children: React.ReactNode;
}> = ({ title, disabled, onClick, className, tone = 'primary', children }) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const toneClass =
    tone === 'danger'
      ? isDark
        ? 'text-red-300'
        : 'text-red-600'
      : tone === 'muted'
        ? colors.textSecondary
        : colors.primaryFg;
  return (
    <Hint content={title} side="top" delay={180} triggerClassName="inline-flex shrink-0">
      <button
        type="button"
        aria-label={title}
        disabled={disabled}
        onClick={onClick}
        className={cn(
          'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors disabled:opacity-50',
          colors.interactiveHover,
          toneClass,
          className,
        )}
      >
        {children}
      </button>
    </Hint>
  );
};

type PlannerFilters = {
  vesselId: string;
  withinDays: string;
  needsReliefOnly: boolean;
};

const EDIT_STATUS_OPTIONS: Array<{ value: CrewReplacementStatus; label: string }> = [
  { value: 'NEEDED', label: 'Needed' },
  { value: 'CANDIDATE', label: 'Candidate' },
  { value: 'CONFIRMED', label: 'Confirmed' },
  { value: 'TRAVEL', label: 'Travel' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function statusBadgeVariant(
  status: CrewReplacementStatus,
): 'default' | 'warning' | 'danger' | 'success' | 'info' {
  switch (status) {
    case 'NEEDED':
      return 'danger';
    case 'CANDIDATE':
      return 'warning';
    case 'CONFIRMED':
      return 'info';
    case 'TRAVEL':
      return 'info';
    case 'COMPLETED':
      return 'success';
    case 'CANCELLED':
      return 'default';
    default:
      return 'default';
  }
}

const WORKFLOW_STEPS = [
  { key: 'CONFIRMED', label: 'Confirm', hint: 'Confirm relief' },
  { key: 'TRAVEL', label: 'Travel', hint: 'Mark travel' },
  { key: 'COMPLETED', label: 'S/Off + S/On', hint: 'Sign off / sign on' },
] as const;

type WorkflowStepKey = (typeof WORKFLOW_STEPS)[number]['key'];

function workflowStepIndex(status: CrewReplacementStatus): number {
  switch (status) {
    case 'CANDIDATE':
      return 0;
    case 'CONFIRMED':
      return 1;
    case 'TRAVEL':
      return 2;
    case 'COMPLETED':
      return 3;
    default:
      return -1;
  }
}

const WorkflowFlowchart: React.FC<{
  status: CrewReplacementStatus;
  busy: boolean;
  onAdvance: (status: CrewReplacementStatus) => void;
  onComplete: () => void;
  onTickets?: () => void;
}> = ({ status, busy, onAdvance, onComplete, onTickets }) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const current = workflowStepIndex(status);
  if (current < 0) return null;

  const stepState = (stepIdx: number): 'done' | 'current' | 'upcoming' => {
    if (stepIdx < current) return 'done';
    if (stepIdx === current) return 'current';
    return 'upcoming';
  };

  const backStatus: CrewReplacementStatus | null =
    status === 'CONFIRMED'
      ? 'CANDIDATE'
      : status === 'TRAVEL'
        ? 'CONFIRMED'
        : status === 'COMPLETED'
          ? 'TRAVEL'
          : null;

  const handleStepClick = (step: WorkflowStepKey, stepIdx: number) => {
    if (busy || stepState(stepIdx) !== 'current') return;
    if (step === 'COMPLETED') onComplete();
    else onAdvance(step);
  };

  return (
    <div
      className={cn(
        'flex items-center gap-1.5 rounded-lg border px-2 py-2',
        colors.border,
        isDark ? 'bg-zinc-900/50' : 'bg-slate-50',
      )}
    >
      <IconBtn
        title="Back one step"
        tone="muted"
        disabled={busy || !backStatus}
        onClick={() => backStatus && onAdvance(backStatus)}
        className={cn(!backStatus && 'invisible pointer-events-none')}
      >
        <ChevronLeftIcon className="h-5 w-5" />
      </IconBtn>

      <div className="flex min-w-0 flex-1 items-stretch overflow-x-auto">
        {WORKFLOW_STEPS.map((step, idx) => {
          const state = stepState(idx);
          const clickable = state === 'current';
          const connectorActive =
            stepState(idx - 1) === 'done' || state === 'done' || state === 'current';
          return (
            <React.Fragment key={step.key}>
              {idx > 0 ? (
                <div className="flex items-center px-1 shrink-0" aria-hidden>
                  <div
                    className={cn(
                      'h-0.5 w-4 sm:w-6 rounded-full',
                      connectorActive
                        ? 'bg-sky-500'
                        : isDark
                          ? 'bg-zinc-600'
                          : 'bg-slate-300',
                    )}
                  />
                  <ChevronRightIcon
                    className={cn(
                      'h-3.5 w-3.5 -ml-0.5',
                      connectorActive
                        ? isDark
                          ? 'text-sky-400'
                          : 'text-sky-600'
                        : colors.textSecondary,
                    )}
                  />
                </div>
              ) : null}
              <Hint
                content={state === 'current' ? step.hint : step.label}
                side="top"
                delay={180}
                triggerClassName="flex min-w-[5.5rem] flex-1"
              >
                <button
                  type="button"
                  disabled={busy || !clickable}
                  onClick={() => handleStepClick(step.key, idx)}
                  className={cn(
                    'relative flex w-full min-w-[5.5rem] flex-1 flex-col items-center justify-center gap-0.5 rounded-lg border px-2 py-1.5 text-center transition-colors',
                    state === 'current' &&
                      (isDark
                        ? 'border-sky-400 bg-sky-950/60 text-sky-200 ring-1 ring-sky-500/40'
                        : 'border-sky-400 bg-sky-50 text-sky-900 ring-1 ring-sky-300'),
                    state === 'done' &&
                      (isDark
                        ? 'border-emerald-700/60 bg-emerald-950/30 text-emerald-200'
                        : 'border-emerald-300 bg-emerald-50 text-emerald-900'),
                    state === 'upcoming' &&
                      (isDark
                        ? 'border-zinc-700 bg-zinc-900/40 text-zinc-500'
                        : 'border-slate-200 bg-white text-slate-400'),
                    clickable && !busy ? 'cursor-pointer' : 'cursor-default',
                    busy && 'opacity-60',
                  )}
                >
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold leading-tight">
                    {state === 'done' ? <CheckIcon className="h-3.5 w-3.5 shrink-0" /> : null}
                    {state === 'current' ? (
                      <span
                        className={cn(
                          'inline-block h-1.5 w-1.5 rounded-full shrink-0',
                          isDark ? 'bg-sky-300' : 'bg-sky-600',
                        )}
                      />
                    ) : null}
                    {step.label}
                  </span>
                  <span className={cn('text-[10px] leading-tight', colors.textSecondary)}>
                    {state === 'done' ? 'Done' : state === 'current' ? 'Click to proceed' : 'Waiting'}
                  </span>
                </button>
              </Hint>
            </React.Fragment>
          );
        })}
      </div>

      {onTickets && (status === 'CONFIRMED' || status === 'TRAVEL' || status === 'COMPLETED') ? (
        <Hint content="Crew Changes Cost" side="top" delay={180} triggerClassName="inline-flex shrink-0">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={onTickets}
            className="shrink-0 h-8 w-8 p-0"
            aria-label="Crew Changes Cost"
          >
            <TicketIcon className="h-4 w-4" />
          </Button>
        </Hint>
      ) : null}
    </div>
  );
};

function MetaBits({ bits }: { bits: Array<string | null | undefined> }) {
  const { colors } = useTheme();
  const clean = bits.filter((b): b is string => !!b && b.trim().length > 0);
  if (!clean.length) return null;
  return (
    <div className={cn('text-xs flex flex-wrap gap-x-2 gap-y-0.5', colors.textSecondary)}>
      {clean.map((b) => (
        <span key={b}>{b}</span>
      ))}
    </div>
  );
}

function daysUntilIso(iso: string | null | undefined, today = todayIso()): number | null {
  if (!iso) return null;
  const a = Date.parse(`${today}T00:00:00Z`);
  const b = Date.parse(`${iso.slice(0, 10)}T00:00:00Z`);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return Math.round((b - a) / 86_400_000);
}

function OnboardPersonCell({
  row,
  notes,
  withinDays,
  from,
}: {
  row: CrewVesselListRow;
  notes?: string | null;
  withinDays: number;
  from: string;
}) {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const urgent = needsRelief(row, withinDays);

  const dateTone = (days: number | null) => {
    if (days == null) return colors.textMuted;
    if (days < 0) return isDark ? 'text-red-300' : 'text-red-700';
    if (days <= withinDays) return isDark ? 'text-amber-300' : 'text-amber-700';
    return colors.textMuted;
  };

  const dateBit = (label: string, iso: string | null | undefined) => {
    if (!iso) return null;
    const days = daysUntilIso(iso);
    const hint =
      days == null
        ? null
        : days < 0
          ? `${Math.abs(days)}d overdue`
          : days === 0
            ? 'today'
            : `${days}d`;
    return (
      <span key={label} className={cn('inline-flex items-baseline gap-1 tabular-nums', dateTone(days))}>
        <span className={cn('font-semibold uppercase tracking-wide text-[10px]', colors.textMuted)}>
          {label}
        </span>
        <span className="text-[11px] font-medium">{formatDate(iso)}</span>
        {hint ? <span className="text-[10px] opacity-80">· {hint}</span> : null}
      </span>
    );
  };

  return (
    <div
      className={cn(
        'min-w-0 rounded-md border px-2 py-1',
        colors.border,
        urgent
          ? isDark
            ? 'border-amber-500/40 bg-amber-950/20'
            : 'border-amber-300/70 bg-amber-50/60'
          : isDark
            ? 'bg-zinc-900/30'
            : 'bg-slate-50/70',
      )}
    >
      <div className="flex min-w-0 items-center gap-1.5">
        <Link
          to={`/crew/${row.crewId}`}
          state={{ from }}
          className={cn('min-w-0 truncate text-sm font-semibold leading-tight hover:underline', colors.primaryFg)}
        >
          <Hint content={crewName(row)} onlyWhenTruncated side="top" delay={180} triggerClassName="block min-w-0 truncate">
            <span className="block truncate">{crewName(row)}</span>
          </Hint>
        </Link>
        {row.nationality ? (
          <span className={cn('shrink-0 text-[11px]', colors.textMuted)}>{row.nationality}</span>
        ) : null}
        {row.age != null ? (
          <span className={cn('shrink-0 text-[11px] tabular-nums', colors.textMuted)}>{row.age}y</span>
        ) : null}
        {row.monthsOnboard != null ? (
          <span className={cn('shrink-0 text-[11px] tabular-nums', colors.textMuted)}>
            {row.monthsOnboard}m
          </span>
        ) : null}
        {urgent ? (
          <Badge variant="warning" className="ml-auto shrink-0 rounded px-1 py-0 text-[9px] leading-4">
            Relief
          </Badge>
        ) : null}
      </div>

      {(row.coeDate || row.intentionSignOffDate || row.extCoeDate) && (
        <div className={cn('mt-0.5 flex flex-wrap items-center gap-x-2.5 gap-y-0')}>
          {dateBit('COE', row.coeDate)}
          {dateBit('Int.', row.intentionSignOffDate)}
          {dateBit('Ext.', row.extCoeDate)}
        </div>
      )}

      {notes?.trim() ? (
        <Hint content={notes.trim()} onlyWhenTruncated side="top" delay={180} triggerClassName="block min-w-0">
          <div className={cn('mt-0.5 truncate text-[10px] italic leading-tight', colors.textMuted)}>
            {notes.trim()}
          </div>
        </Hint>
      ) : null}
    </div>
  );
}

function reliefSummary(row: ReliefBoardRow): string {
  return [
    row.rank,
    row.nationality,
    row.age != null ? `Age ${row.age}` : null,
    row.daysAshore != null ? `${row.daysAshore}d ashore` : null,
    row.notRecommended ? 'NR' : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

function IncomingProfile({ row }: { row: ReliefBoardRow }) {
  const { colors } = useTheme();
  const avail =
    row.availableFrom || row.availableTo
      ? `Avail ${row.availableFrom ? formatDate(row.availableFrom) : '…'} → ${
          row.availableTo ? formatDate(row.availableTo) : '…'
        }`
      : row.availabilityStatus;

  return (
    <div className="min-w-0 space-y-1.5">
      <div className="flex flex-wrap items-center gap-1.5">
        {row.notRecommended ? (
          <Badge variant="danger" className="rounded-md px-1.5 py-0 text-[10px]">
            NR
          </Badge>
        ) : null}
        {row.toBePromoted ? (
          <Badge variant="info" className="rounded-md px-1.5 py-0 text-[10px]">
            Promote
          </Badge>
        ) : null}
        {row.expiredCerts > 0 ? (
          <Badge variant="danger" className="rounded-md px-1.5 py-0 text-[10px]">
            {row.expiredCerts} expired cert{row.expiredCerts === 1 ? '' : 's'}
          </Badge>
        ) : null}
        {row.availabilityStatus ? (
          <Badge variant="secondary" className="rounded-md px-1.5 py-0 text-[10px]">
            {row.availabilityStatus}
          </Badge>
        ) : null}
      </div>
      <MetaBits
        bits={[
          row.rank,
          row.nationality,
          row.age != null ? `Age ${row.age}` : null,
          row.daysAshore != null ? `${row.daysAshore}d ashore` : null,
          row.monthsInRank != null ? `${row.monthsInRank}m in rank` : null,
        ]}
      />
      <MetaBits
        bits={[
          avail,
          row.lastVessel ? `Last ${row.lastVessel}` : null,
          row.lastSignOffDate ? `S/Off ${formatDate(row.lastSignOffDate)}` : null,
          row.monthsLastTour != null ? `Last tour ${row.monthsLastTour}m` : null,
        ]}
      />
      <MetaBits
        bits={[
          row.medicalNextDue
            ? `Medical ${formatDate(row.medicalNextDue)}${row.medicalResult ? ` · ${row.medicalResult}` : ''}`
            : row.medicalStatus,
          row.nextCertExpiry ? `Next cert ${formatDate(row.nextCertExpiry)}` : null,
          row.overallEvaluation
            ? `Appraisal ${row.overallEvaluation}${
                row.lastAppraisalDate ? ` (${formatDate(row.lastAppraisalDate)})` : ''
              }`
            : null,
          row.manningAgent,
          row.nearestAirport ? `Airport ${row.nearestAirport}` : null,
          row.nearestPort ? `Port ${row.nearestPort}` : null,
          row.lastEngine ? `Eng ${row.lastEngine}` : null,
          row.lastDwt != null ? `DWT ${row.lastDwt}` : null,
          row.vesselTypes,
          row.mobile,
          row.email,
        ]}
      />
      {row.availabilityNotes ? (
        <div className={cn('text-[11px] italic', colors.textSecondary)}>{row.availabilityNotes}</div>
      ) : null}
    </div>
  );
}

type MatrixRuleResult = {
  ruleId?: number;
  kind?: string;
  metric?: string | null;
  label: string;
  status: string;
  detail?: string;
  minimum?: number | null;
  actual?: number | null;
  persons?: { crewId: number; name: string; value: number }[];
};

type MatrixCrewBadge = {
  status: string;
  summary: string;
  /** Fail/warn/pending lines used on assign confirm. */
  lines: string[];
  results: MatrixRuleResult[];
};

/** Slot-scoped key — same crew can score differently on two open slots. */
function slotMatrixKey(outgoingTransactionId: number, crewId: number): string {
  return `${outgoingTransactionId}:${crewId}`;
}

function lookupSlotOrPoolMatrix(
  outgoingTransactionId: number | null | undefined,
  crewId: number,
  slotMap: Map<string, MatrixCrewBadge>,
  poolMap: Map<number, MatrixCrewBadge>,
): MatrixCrewBadge | null {
  if (outgoingTransactionId != null) {
    const slot = slotMap.get(slotMatrixKey(outgoingTransactionId, crewId));
    if (slot) return slot;
  }
  return poolMap.get(crewId) ?? null;
}


function matrixBadgeTone(status: string) {
  if (status === 'pass') return 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300';
  if (status === 'warning') return 'bg-amber-500/15 text-amber-800 dark:text-amber-300';
  if (status === 'fail') return 'bg-red-500/15 text-red-700 dark:text-red-300';
  if (status === 'notReady') return 'bg-slate-500/15 text-slate-600 dark:text-slate-300';
  return 'bg-slate-500/15 text-slate-600 dark:text-slate-300';
}

function matrixUnit(metric?: string | null): string {
  if (!metric) return '';
  if (metric.startsWith('MONTHS_')) return 'months';
  if (metric.startsWith('YEARS_')) return 'years';
  return '';
}

function formatMatrixValue(value: number | null | undefined, metric?: string | null): string {
  if (value == null || Number.isNaN(value)) return '—';
  const unit = matrixUnit(metric);
  const n = Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, '');
  return unit ? `${n} ${unit}` : n;
}

function matrixStatusLabel(status: string): string {
  if (status === 'pass') return 'Pass';
  if (status === 'fail') return 'Fail';
  if (status === 'warning') return 'Warning';
  if (status === 'notReady') return 'Pending';
  return status;
}

const MatrixStatusBadge: React.FC<{
  badge: MatrixCrewBadge;
  className?: string;
  onOpen: () => void;
}> = ({ badge, className, onOpen }) => (
  <Hint
    content="Click for required vs actual on each rule"
    side="left"
    delay={180}
    triggerClassName="inline-flex shrink-0"
  >
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onOpen();
      }}
      onMouseDown={(e) => e.stopPropagation()}
      className={cn(
        'rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase transition-opacity hover:opacity-80',
        matrixBadgeTone(badge.status),
        className,
      )}
      aria-label={`Matrix ${badge.status} — view rule details`}
    >
      {badge.status}
    </button>
  </Hint>
);

function matrixStatusSortKey(status: string): number {
  if (status === 'fail') return 0;
  if (status === 'warning') return 1;
  if (status === 'notReady') return 2;
  if (status === 'pass') return 3;
  return 4;
}

function matrixKindLabel(kind?: string): string {
  if (kind === 'INDIVIDUAL') return 'Individual';
  if (kind === 'COMBINED') return 'Combined';
  if (kind === 'TOGETHER') return 'Together';
  if (kind === 'STAGGER') return 'Stagger';
  return kind || 'Rule';
}

function matrixRuleGap(r: MatrixRuleResult): number | null {
  if (r.actual == null || r.minimum == null) return null;
  return Math.round((r.actual - r.minimum) * 100) / 100;
}

function formatMatrixRuleLine(r: MatrixRuleResult): string {
  const gap = matrixRuleGap(r);
  const req =
    r.kind === 'STAGGER' ? 'Do not change both' : formatMatrixValue(r.minimum, r.metric);
  const have =
    r.kind === 'STAGGER'
      ? r.actual === 1
        ? 'Both open'
        : 'OK'
      : formatMatrixValue(r.actual, r.metric);
  const gapTxt =
    gap == null ? '—' : `${gap >= 0 ? '+' : ''}${formatMatrixValue(gap, r.metric)}`;
  return `${matrixStatusLabel(r.status)} · ${r.label} [${matrixKindLabel(r.kind)}] — required ${req}, have ${have}, gap ${gapTxt}`;
}

function MatrixProgressBar({
  actual,
  minimum,
  status,
}: {
  actual: number | null | undefined;
  minimum: number | null | undefined;
  status: string;
}) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  if (actual == null || minimum == null || minimum <= 0) return null;
  const pct = Math.max(0, Math.min(100, (actual / minimum) * 100));
  const fill =
    status === 'fail'
      ? isDark
        ? 'bg-red-500'
        : 'bg-red-600'
      : status === 'warning'
        ? isDark
          ? 'bg-amber-500'
          : 'bg-amber-500'
        : status === 'pass'
          ? isDark
            ? 'bg-emerald-500'
            : 'bg-emerald-600'
          : isDark
            ? 'bg-slate-500'
            : 'bg-slate-400';
  return (
    <div
      className={cn(
        'mt-2 h-1.5 w-full overflow-hidden rounded-full',
        isDark ? 'bg-zinc-800' : 'bg-slate-200',
      )}
      title={`${pct.toFixed(0)}% of required`}
    >
      <div className={cn('h-full rounded-full transition-all', fill)} style={{ width: `${pct}%` }} />
    </div>
  );
}

type MatrixDetailState = {
  crewName: string;
  badge: MatrixCrewBadge;
  vesselName?: string | null;
  rankLabel?: string | null;
  asOf?: string | null;
  noMatrix?: boolean;
};

const MatrixRulesDetailModal: React.FC<{
  open: boolean;
  detail: MatrixDetailState | null;
  fromPath?: string;
  onClose: () => void;
}> = ({ open, detail, fromPath, onClose }) => {
  const { colors } = useTheme();
  const [copyMsg, setCopyMsg] = useState<string | null>(null);
  const badge = detail?.badge ?? null;

  const sortedResults = useMemo(() => {
    if (!badge?.results?.length) return [];
    return [...badge.results].sort(
      (a, b) => matrixStatusSortKey(a.status) - matrixStatusSortKey(b.status),
    );
  }, [badge?.results]);

  const contextLine = useMemo(() => {
    if (!detail) return '';
    return [
      detail.vesselName || null,
      detail.rankLabel ? `Rank ${detail.rankLabel}` : null,
      detail.asOf ? `As of ${formatDate(detail.asOf)}` : null,
    ]
      .filter(Boolean)
      .join(' · ');
  }, [detail]);

  const buildSummaryText = useCallback(() => {
    if (!detail || !badge) return '';
    const lines = [
      `Matrix rules · ${detail.crewName}`,
      contextLine || null,
      `Overall: ${matrixStatusLabel(badge.status)}${badge.summary ? ` · ${badge.summary}` : ''}`,
      '',
      ...sortedResults.map(formatMatrixRuleLine),
    ].filter((x) => x != null);
    return lines.join('\n');
  }, [detail, badge, contextLine, sortedResults]);

  const handleCopy = useCallback(async () => {
    const text = buildSummaryText();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopyMsg('Copied');
      window.setTimeout(() => setCopyMsg(null), 1600);
    } catch {
      setCopyMsg('Copy failed');
      window.setTimeout(() => setCopyMsg(null), 1600);
    }
  }, [buildSummaryText]);

  if (!badge || !detail) return null;

  return (
    <Modal isOpen={open} onClose={onClose} size="lg">
      <ModalHeader>
        <ModalTitle>Matrix rules · {detail.crewName}</ModalTitle>
      </ModalHeader>
      <ModalContent className="max-h-[70vh] overflow-auto">
        {contextLine ? (
          <p className={cn('mb-2 text-xs', colors.textSecondary)}>{contextLine}</p>
        ) : null}
        <p className={cn('mb-3 text-xs', colors.textSecondary)}>
          Overall:{' '}
          <span
            className={cn(
              'rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase',
              matrixBadgeTone(badge.status),
            )}
          >
            {badge.status}
          </span>
          {badge.summary ? ` · ${badge.summary}` : null}
        </p>
        {sortedResults.length === 0 ? (
          <p className={cn('text-sm', colors.textSecondary)}>
            {detail.noMatrix
              ? 'No manning matrix is configured for this vessel (or its vessel type). Configure rules under Crew Matrix Rules.'
              : 'No matrix rules applied to this rank for the selected vessel.'}
          </p>
        ) : (
          <div className="space-y-2">
            {sortedResults.map((r, idx) => {
              const gap = matrixRuleGap(r);
              return (
                <div
                  key={r.ruleId ?? `${r.label}-${idx}`}
                  className={cn('rounded-lg border px-3 py-2.5', colors.border, colors.surface)}
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className={cn('text-sm font-medium', colors.text)}>{r.label}</span>
                        {r.kind ? (
                          <span
                            className={cn(
                              'rounded px-1.5 py-0.5 text-[10px] font-medium',
                              colors.textMuted,
                              colors.border,
                              'border',
                            )}
                          >
                            {matrixKindLabel(r.kind)}
                          </span>
                        ) : null}
                      </div>
                      {r.detail ? (
                        <p className={cn('mt-0.5 text-[11px]', colors.textSecondary)}>{r.detail}</p>
                      ) : null}
                    </div>
                    <span
                      className={cn(
                        'shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase',
                        matrixBadgeTone(r.status),
                      )}
                    >
                      {matrixStatusLabel(r.status)}
                    </span>
                  </div>
                  <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <div className={cn('text-[10px] uppercase tracking-wide', colors.textMuted)}>
                        Required
                      </div>
                      <div className={cn('mt-0.5 font-medium tabular-nums', colors.text)}>
                        {r.kind === 'STAGGER'
                          ? 'Do not change both'
                          : formatMatrixValue(r.minimum, r.metric)}
                      </div>
                    </div>
                    <div>
                      <div className={cn('text-[10px] uppercase tracking-wide', colors.textMuted)}>
                        Have
                      </div>
                      <div className={cn('mt-0.5 font-medium tabular-nums', colors.text)}>
                        {r.kind === 'STAGGER'
                          ? r.actual === 1
                            ? 'Both open'
                            : 'OK'
                          : formatMatrixValue(r.actual, r.metric)}
                      </div>
                    </div>
                    <div>
                      <div className={cn('text-[10px] uppercase tracking-wide', colors.textMuted)}>
                        Gap
                      </div>
                      <div
                        className={cn(
                          'mt-0.5 font-medium tabular-nums',
                          gap == null
                            ? colors.textSecondary
                            : gap >= 0
                              ? 'text-emerald-700 dark:text-emerald-300'
                              : 'text-red-700 dark:text-red-300',
                        )}
                      >
                        {gap == null
                          ? '—'
                          : `${gap >= 0 ? '+' : ''}${formatMatrixValue(gap, r.metric)}`}
                      </div>
                    </div>
                  </div>
                  {r.kind !== 'STAGGER' ? (
                    <MatrixProgressBar actual={r.actual} minimum={r.minimum} status={r.status} />
                  ) : null}
                  {r.persons && r.persons.length > 0 ? (
                    <ul
                      className={cn(
                        'mt-2 space-y-0.5 border-t pt-2 text-[11px]',
                        colors.border,
                        colors.textSecondary,
                      )}
                    >
                      {r.persons.map((p) => (
                        <li key={p.crewId} className="flex justify-between gap-2">
                          <Link
                            to={`/crew/${p.crewId}`}
                            state={fromPath ? { from: fromPath } : undefined}
                            className={cn('min-w-0 truncate hover:underline', colors.primaryFg)}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {p.name}
                          </Link>
                          <span className="shrink-0 tabular-nums">
                            {formatMatrixValue(p.value, r.metric)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </ModalContent>
      <ModalFooter>
        <div className="mr-auto flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => void handleCopy()}>
            {copyMsg || 'Copy summary'}
          </Button>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={onClose}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const ReliefCard: React.FC<{
  row: ReliefBoardRow;
  open: boolean;
  onToggle: (open: boolean) => void;
  matrixBadge?: MatrixCrewBadge | null;
  onOpenMatrix?: (badge: MatrixCrewBadge) => void;
}> = ({ row, open, onToggle, matrixBadge, onOpenMatrix }) => {
  const { colors } = useTheme();
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData(DND_MIME, String(row.crewId));
        e.dataTransfer.setData('text/plain', row.name);
        e.dataTransfer.effectAllowed = 'copy';
      }}
      className="w-full cursor-grab active:cursor-grabbing"
      title="Drag onto a replacement slot"
    >
      <AccordionItem
        title={row.name}
        description={
          [
            reliefSummary(row) || 'Shore candidate',
            matrixBadge?.summary || null,
          ]
            .filter(Boolean)
            .join(' · ')
        }
        open={open}
        onToggle={onToggle}
        headerClassName="px-3 py-2"
        headerActionClassName="px-1"
        className="w-full select-none"
        headerAction={
          matrixBadge && onOpenMatrix ? (
            <MatrixStatusBadge badge={matrixBadge} onOpen={() => onOpenMatrix(matrixBadge)} />
          ) : undefined
        }
      >
        <IncomingProfile row={row} />
        {matrixBadge && onOpenMatrix ? (
          <button
            type="button"
            className={cn(
              'mt-2 text-left text-[11px] underline-offset-2 hover:underline',
              colors.textSecondary,
            )}
            onClick={() => onOpenMatrix(matrixBadge)}
          >
            View matrix rules (required vs have)
          </button>
        ) : null}
      </AccordionItem>
    </div>
  );
};

const ReplacementSlot: React.FC<{
  line: BoardLine;
  busy: boolean;
  incomingProfile: ReliefBoardRow | null;
  reliefById: Map<number, ReliefBoardRow>;
  lookupMatrix: (crewId: number) => MatrixCrewBadge | null;
  onOpenMatrix: (
    crewName: string,
    badge: MatrixCrewBadge,
    ctx?: { rankLabel?: string | null; asOf?: string | null },
  ) => void;
  conflictDuplicate?: boolean;
  conflictOverlap?: boolean;
  onDropCrew: (line: BoardLine, crewId: number) => void;
  onOpenPick: (line: BoardLine) => void;
  onOpenEdit: (row: CrewReplacementRow) => void;
  onOpenHistory: (row: CrewReplacementRow) => void;
  onClear: (line: BoardLine) => void;
  onAdvance: (row: CrewReplacementRow, status: CrewReplacementStatus) => void;
  onComplete: (row: CrewReplacementRow) => void;
  onTickets: (row: CrewReplacementRow) => void;
  onSelectCandidate: (row: CrewReplacementRow, candidate: CrewReplacementCandidateRow) => void;
  onRemoveCandidate: (row: CrewReplacementRow, candidate: CrewReplacementCandidateRow) => void;
}> = ({
  line,
  busy,
  incomingProfile,
  reliefById,
  lookupMatrix,
  onOpenMatrix,
  conflictDuplicate,
  conflictOverlap,
  onDropCrew,
  onOpenPick,
  onOpenEdit,
  onOpenHistory,
  onClear,
  onAdvance,
  onComplete,
  onTickets,
  onSelectCandidate,
  onRemoveCandidate,
}) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const [over, setOver] = useState(false);
  const { listReturnPath } = useCrewReportOpen();
  const repl = line.replacement;
  const candidates = (repl?.candidates ?? []).filter((c) => !c.isSelected);
  const incomingMatrix =
    repl?.incomingCrewId != null ? lookupMatrix(repl.incomingCrewId) : null;

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setOver(false);
    const raw = e.dataTransfer.getData(DND_MIME) || e.dataTransfer.getData('text/plain');
    const crewId = Number(raw);
    if (!Number.isFinite(crewId) || crewId <= 0) return;
    if (crewId === line.onboard.crewId) return;
    onDropCrew(line, crewId);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setOver(true);
  };

  const candidatesBlock =
    candidates.length > 0 ? (
      <div className="space-y-1.5">
        <div className={cn('text-[10px] font-semibold uppercase tracking-wide px-0.5', colors.textMuted)}>
          Possible replacements ({candidates.length})
        </div>
        <div className="flex flex-col gap-1.5">
          {candidates.map((c) => {
            const profile = reliefById.get(c.crewId);
            const matrix = lookupMatrix(c.crewId);
            const warnings = profile
              ? getAssignWarnings(line.rankId, line.rankLabel, profile, undefined, matrix?.lines)
              : [];
            return (
            <div
              key={c.id}
              className={cn(
                'flex items-center gap-1.5 rounded-lg border px-2 py-1.5',
                isDark
                  ? 'border-zinc-700 bg-zinc-900/40'
                  : 'border-slate-200 bg-white',
              )}
            >
              <div className="min-w-0 flex-1">
                <Hint
                  content={c.crewRef ? `${c.crewName} (${c.crewRef})` : c.crewName}
                  onlyWhenTruncated
                  side="top"
                  delay={180}
                  triggerClassName="block min-w-0"
                >
                  <Link
                    to={`/crew/${c.crewId}`}
                    state={{ from: listReturnPath }}
                    className={cn('block truncate text-sm hover:underline', colors.primaryFg)}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {c.crewName}
                  </Link>
                </Hint>
                {(c.rank || c.nationalityCode) && (
                  <div className={cn('truncate text-[10px] leading-tight', colors.textMuted)}>
                    {[c.rank, c.nationalityCode].filter(Boolean).join(' - ')}
                  </div>
                )}
              </div>
              {matrix ? (
                <MatrixStatusBadge
                  badge={matrix}
                  onOpen={() =>
                    onOpenMatrix(c.crewName, matrix, {
                      rankLabel: line.rankLabel,
                      asOf: repl?.plannedSignOnDate || repl?.plannedSignOffDate || null,
                    })
                  }
                />
              ) : null}
              {warnings.length > 0 ? (
                <Hint content={warnings.join('\n')} side="top" delay={120} triggerClassName="inline-flex shrink-0">
                  <span
                    className={cn(
                      'inline-flex shrink-0',
                      isDark ? 'text-amber-400' : 'text-amber-600',
                    )}
                    aria-label={warnings.join('. ')}
                  >
                    <WarningIcon className="h-4 w-4" />
                  </span>
                </Hint>
              ) : null}
              {repl ? (
                <IconBtn
                  title={`Select ${c.crewName} as relief`}
                  disabled={busy}
                  onClick={() => onSelectCandidate(repl, c)}
                >
                  <CheckIcon className="h-4 w-4" />
                </IconBtn>
              ) : null}
              {repl ? (
                <IconBtn
                  title={`Remove ${c.crewName}`}
                  disabled={busy}
                  tone="muted"
                  onClick={() => onRemoveCandidate(repl, c)}
                >
                  <XIcon className="h-4 w-4" />
                </IconBtn>
              ) : null}
            </div>
            );
          })}
        </div>
      </div>
    ) : null;

  if (repl?.incomingCrewId && repl.incomingCrewName) {
    const title = (
      <span className="inline-flex min-w-0 flex-wrap items-center gap-1.5">
        <Link
          to={`/crew/${repl.incomingCrewId}`}
          state={{ from: listReturnPath }}
          className={cn('hover:underline truncate', colors.primaryFg)}
          onClick={(e) => e.stopPropagation()}
        >
          {repl.incomingCrewName}
        </Link>
        <Badge variant={statusBadgeVariant(repl.status)} className="rounded-md px-1.5 py-0 text-[10px]">
          {repl.status}
        </Badge>
        {incomingMatrix ? (
          <MatrixStatusBadge
            badge={incomingMatrix}
            onOpen={() =>
              onOpenMatrix(repl.incomingCrewName || 'Relief', incomingMatrix, {
                rankLabel: line.rankLabel,
                asOf: repl.plannedSignOnDate || repl.plannedSignOffDate || null,
              })
            }
          />
        ) : null}
        {conflictDuplicate ? (
          <Badge variant="danger" className="rounded-md px-1.5 py-0 text-[10px]">
            Dup relief
          </Badge>
        ) : null}
        {conflictOverlap ? (
          <Badge variant="warning" className="rounded-md px-1.5 py-0 text-[10px]">
            Overlap
          </Badge>
        ) : null}
      </span>
    );
    const description = [
      incomingProfile ? reliefSummary(incomingProfile) : null,
      incomingMatrix?.summary || null,
      repl.plannedSignOffDate ? `S/Off ${formatDate(repl.plannedSignOffDate)}` : null,
      repl.plannedSignOnDate ? `S/On ${formatDate(repl.plannedSignOnDate)}` : null,
      repl.overlapDays != null ? `Overlap ${repl.overlapDays}d` : null,
      repl.changePort,
      repl.notes,
    ]
      .filter(Boolean)
      .join(' · ');

    return (
      <div className="space-y-2">
        <Hint
          content="Drop to add another possible replacement"
          side="top"
          delay={200}
          triggerClassName="block w-full"
        >
          <div
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData(DND_MIME, String(repl.incomingCrewId));
              e.dataTransfer.setData('text/plain', repl.incomingCrewName || '');
              e.dataTransfer.effectAllowed = 'copy';
            }}
            onDragOver={handleDragOver}
            onDragLeave={() => setOver(false)}
            onDrop={handleDrop}
            className={cn(
              'cursor-grab active:cursor-grabbing',
              over && (isDark ? 'ring-2 ring-sky-500 rounded-lg' : 'ring-2 ring-sky-400 rounded-lg'),
            )}
          >
            <AccordionItem
              title={title}
              description={description || 'Selected relief'}
              defaultOpen={false}
              headerClassName="px-3 py-2"
              headerAction={
                <>
                  <IconBtn title="Add possible replacements" disabled={busy} onClick={() => onOpenPick(line)}>
                    <UserPlusIcon className="h-5 w-5" />
                  </IconBtn>
                  <IconBtn title="Edit replacement" disabled={busy} onClick={() => onOpenEdit(repl)}>
                    <PencilIcon className="h-5 w-5" />
                  </IconBtn>
                  <IconBtn title="History" disabled={busy} tone="muted" onClick={() => onOpenHistory(repl)}>
                    <ClockIcon className="h-5 w-5" />
                  </IconBtn>
                  <IconBtn title="Clear selected relief" disabled={busy} tone="muted" onClick={() => onClear(line)}>
                    <XIcon className="h-5 w-5" />
                  </IconBtn>
                </>
              }
            >
              {incomingProfile ? (
                <IncomingProfile row={incomingProfile} />
              ) : (
                <div className={cn('text-xs', colors.textSecondary)}>
                  Open the crew file for full relief details.
                </div>
              )}
              {incomingMatrix ? (
                <button
                  type="button"
                  className={cn(
                    'mt-2 text-left text-[11px] underline-offset-2 hover:underline',
                    colors.textSecondary,
                  )}
                  onClick={() =>
                    onOpenMatrix(repl.incomingCrewName || 'Relief', incomingMatrix, {
                      rankLabel: line.rankLabel,
                      asOf: repl.plannedSignOnDate || repl.plannedSignOffDate || null,
                    })
                  }
                >
                  View matrix rules (required vs have)
                </button>
              ) : null}
            </AccordionItem>
          </div>
        </Hint>

        {candidatesBlock}

        {repl.status === 'CANDIDATE' ||
        repl.status === 'CONFIRMED' ||
        repl.status === 'TRAVEL' ||
        repl.status === 'COMPLETED' ? (
          <WorkflowFlowchart
            status={repl.status}
            busy={busy}
            onAdvance={(status) => onAdvance(repl, status)}
            onComplete={() => onComplete(repl)}
            onTickets={() => onTickets(repl)}
          />
        ) : null}

        {repl.notes ? (
          <Hint content={repl.notes} onlyWhenTruncated side="top" delay={180} triggerClassName="block">
            <div className={cn('truncate text-xs px-1', colors.textSecondary)}>
              Note: {repl.notes}
            </div>
          </Hint>
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div
        onDragOver={handleDragOver}
        onDragLeave={() => setOver(false)}
        onDrop={handleDrop}
        className={cn(
          'flex items-center gap-2 rounded-lg border border-dashed px-2.5 py-2 min-h-[3rem]',
          over
            ? isDark
              ? 'border-sky-400 bg-sky-950/40'
              : 'border-sky-400 bg-sky-50'
            : isDark
              ? 'border-zinc-600 bg-zinc-900/30'
              : 'border-slate-300 bg-slate-50',
        )}
      >
        <span className={cn('flex-1 text-xs', colors.textSecondary)}>
          {over
            ? 'Drop to add as possible replacement'
            : candidates.length
              ? 'Add more possibles, or select one as relief'
              : 'Drop possibles or pick…'}
        </span>
        {repl ? (
          <IconBtn title="History" disabled={busy} tone="muted" onClick={() => onOpenHistory(repl)}>
            <ClockIcon className="h-5 w-5" />
          </IconBtn>
        ) : null}
        <IconBtn title="Add possible replacements" disabled={busy} onClick={() => onOpenPick(line)}>
          <UserPlusIcon className="h-5 w-5" />
        </IconBtn>
      </div>
      {candidatesBlock}
    </div>
  );
};

const ReplacementPlannerPage: React.FC = () => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const { listReturnPath } = useCrewReportOpen();
  const [searchParams] = useSearchParams();
  const [persisted, setPersisted] = usePersistedListFilters<PlannerFilters>(STORAGE_KEY);
  const {
    splitRef,
    panelWidth,
    panelResizing,
    startResize,
    minWidth,
    maxWidth,
  } = useResizablePanel({
    storageKey: 'crew-replacement-planner-split',
    defaultWidth: 384,
    minWidth: 260,
    maxWidth: 640,
    side: 'right',
  });

  const initialVessel = searchParams.get('vesselId')?.trim() || persisted?.vesselId || '';
  const [vesselId, setVesselId] = useState(initialVessel);
  const [withinDays, setWithinDays] = useState(persisted?.withinDays || '90');
  const [needsReliefOnly, setNeedsReliefOnly] = useState(persisted?.needsReliefOnly ?? true);
  const [applied, setApplied] = useState<PlannerFilters | null>(() =>
    initialVessel
      ? {
          vesselId: initialVessel,
          withinDays: persisted?.withinDays || '90',
          needsReliefOnly: persisted?.needsReliefOnly ?? true,
        }
      : null,
  );

  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [seedMsg, setSeedMsg] = useState<string | null>(null);
  const [seeding, setSeeding] = useState(false);
  const [rankFilter, setRankFilter] = useState<string>('');
  const [poolOpenIds, setPoolOpenIds] = useState<Set<number>>(() => new Set());
  const [poolMatrixByCrew, setPoolMatrixByCrew] = useState<Map<number, MatrixCrewBadge>>(
    () => new Map(),
  );
  const [slotMatrixByKey, setSlotMatrixByKey] = useState<Map<string, MatrixCrewBadge>>(
    () => new Map(),
  );
  const [vesselHasMatrix, setVesselHasMatrix] = useState<boolean | null>(null);
  const [vesselMatrixName, setVesselMatrixName] = useState<string | null>(null);
  const [matrixDetail, setMatrixDetail] = useState<MatrixDetailState | null>(null);
  const [rankingPool, setRankingPool] = useState(false);
  const [scoringBoard, setScoringBoard] = useState(false);
  const [rankSlotPick, setRankSlotPick] = useState<BoardLine[] | null>(null);
  const [pickLine, setPickLine] = useState<BoardLine | null>(null);
  const [editRow, setEditRow] = useState<CrewReplacementRow | null>(null);
  const [pendingAssign, setPendingAssign] = useState<{
    line: BoardLine;
    crewId: number;
    name: string;
    warnings: string[];
    mode: 'possible' | 'select';
  } | null>(null);
  const [completeTarget, setCompleteTarget] = useState<CrewReplacementRow | null>(null);
  const [undoCompleteTarget, setUndoCompleteTarget] = useState<CrewReplacementRow | null>(null);
  const [clearTarget, setClearTarget] = useState<BoardLine | null>(null);
  const [pendingSelectCandidate, setPendingSelectCandidate] = useState<{
    row: CrewReplacementRow;
    candidate: CrewReplacementCandidateRow;
    warnings: string[];
  } | null>(null);
  const [pendingRemoveCandidate, setPendingRemoveCandidate] = useState<{
    row: CrewReplacementRow;
    candidate: CrewReplacementCandidateRow;
  } | null>(null);
  const [workflowBusy, setWorkflowBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [historyTarget, setHistoryTarget] = useState<{
    mode: 'slot' | 'vessel';
    replacementId?: number;
    title: string;
  } | null>(null);
  const [editForm, setEditForm] = useState({
    plannedSignOffDate: '',
    plannedSignOnDate: '',
    changePortId: '',
    status: 'NEEDED' as CrewReplacementStatus,
    overlapDays: '',
    notes: '',
  });

  const today = todayIso();

  const { data: lookups } = useSWR<{
    ranks: Array<{ id: number; name: string; abrv?: string | null; sortOrder?: number }>;
  }>('/crew/lookups', fetcher, { revalidateOnFocus: false });

  const vesselListUrl = applied
    ? `/crew/vessel-list?vesselId=${encodeURIComponent(applied.vesselId)}&periodFrom=${today}&periodTo=${today}`
    : null;
  const replUrl = applied
    ? `/crew/replacements?vesselId=${encodeURIComponent(applied.vesselId)}&includeCancelled=0`
    : null;

  const { data: vesselList, error: vesselError, isLoading: vesselLoading, mutate: mutateVessel } =
    useSWR<CrewVesselListResponse>(vesselListUrl, fetcher, { revalidateOnFocus: false });
  const { data: replData, mutate: mutateRepl } = useSWR<CrewReplacementResponse>(replUrl, fetcher, {
    revalidateOnFocus: false,
  });
  const { data: relief } = useSWR<ReliefBoardResponse>('/crew/reports/relief-board', fetcher, {
    revalidateOnFocus: false,
  });

  const historyUrl = historyTarget
    ? historyTarget.mode === 'slot' && historyTarget.replacementId
      ? `/crew/replacements/${historyTarget.replacementId}/history`
      : applied
        ? `/crew/replacements/history?vesselId=${encodeURIComponent(applied.vesselId)}&take=150`
        : null
    : null;
  const {
    data: historyData,
    error: historyError,
    isLoading: historyLoading,
  } = useSWR<CrewReplacementAuditResponse>(historyUrl, fetcher, { revalidateOnFocus: false });

  const reliefById = useMemo(() => {
    const map = new Map<number, ReliefBoardRow>();
    for (const row of relief?.rows ?? []) map.set(row.crewId, row);
    return map;
  }, [relief?.rows]);

  const poolRows = useMemo(() => {
    const rows = relief?.rows ?? [];
    let filtered = rows;
    if (rankFilter) {
      const rid = Number(rankFilter);
      if (Number.isFinite(rid)) filtered = rows.filter((r) => r.rankId === rid);
    }
    if (poolMatrixByCrew.size === 0) return filtered;
    const rankOf = (status: string) =>
      status === 'pass' ? 0 : status === 'warning' ? 1 : status === 'notReady' ? 2 : 3;
    return [...filtered].sort((a, b) => {
      const sa = poolMatrixByCrew.get(a.crewId)?.status ?? 'zzzz';
      const sb = poolMatrixByCrew.get(b.crewId)?.status ?? 'zzzz';
      const ra = rankOf(sa);
      const rb = rankOf(sb);
      if (ra !== rb) return ra - rb;
      return a.name.localeCompare(b.name);
    });
  }, [relief?.rows, rankFilter, poolMatrixByCrew]);

  const expandPoolAll = useCallback(() => {
    setPoolOpenIds(new Set(poolRows.map((r) => r.crewId)));
  }, [poolRows]);

  const collapsePoolAll = useCallback(() => {
    setPoolOpenIds(new Set());
  }, []);

  const togglePoolOpen = useCallback((crewId: number, open: boolean) => {
    setPoolOpenIds((prev) => {
      const next = new Set(prev);
      if (open) next.add(crewId);
      else next.delete(crewId);
      return next;
    });
  }, []);

  const onboardRows = useMemo(
    () => (vesselList?.rows ?? []).filter((r) => r.status === 'ONB' || (!r.signOffDate && !r.status)),
    [vesselList?.rows],
  );

  const refresh = useCallback(async () => {
    await Promise.all([mutateVessel(), mutateRepl()]);
  }, [mutateVessel, mutateRepl]);

  const handleSearch = useCallback(() => {
    if (!vesselId) return;
    const next = { vesselId, withinDays, needsReliefOnly };
    setApplied(next);
    setPersisted(next);
    setSeedMsg(null);
  }, [vesselId, withinDays, needsReliefOnly, setPersisted]);

  const allLines = useMemo(
    () => buildBoardLines(onboardRows, replData?.rows ?? []),
    [onboardRows, replData?.rows],
  );

  const lines = useMemo(() => {
    if (!applied) return allLines;
    return filterBoardLines(allLines, {
      needsReliefOnly: applied.needsReliefOnly,
      withinDays: Number(applied.withinDays) || 90,
    });
  }, [allLines, applied]);

  const conflicts = useMemo(() => detectConflicts(replData?.rows ?? []), [replData?.rows]);

  /** Fingerprint of who is selected/possible on open slots — drives auto matrix scoring. */
  const boardMatrixScoreKey = useMemo(() => {
    if (!applied?.vesselId || !replData?.rows?.length) return '';
    return replData.rows
      .filter((r) => r.status !== 'CANCELLED' && r.status !== 'COMPLETED')
      .map((r) => {
        const ids = [
          r.incomingCrewId,
          ...(r.candidates ?? []).map((c) => c.crewId),
        ]
          .filter((id): id is number => id != null && id > 0)
          .sort((a, b) => a - b);
        return `${r.id}:${r.outgoingTransactionId}:${ids.join(',')}`;
      })
      .sort()
      .join('|');
  }, [applied?.vesselId, replData?.rows]);

  const openSlotsForRank = useMemo(() => {
    if (!rankFilter) return [] as BoardLine[];
    const rid = Number(rankFilter);
    if (!Number.isFinite(rid)) return [];
    return lines.filter(
      (l) =>
        l.rankId === rid &&
        l.replacement &&
        l.replacement.status !== 'CANCELLED' &&
        l.replacement.status !== 'COMPLETED',
    );
  }, [lines, rankFilter]);

  const buildMatrixBadge = useCallback(
    (c: {
      crewId: number;
      status: string;
      fails: number;
      warnings: number;
      notReady: number;
      results: MatrixRuleResult[];
    }): MatrixCrewBadge => {
      const results = (c.results ?? []).map((r) => ({
        ruleId: r.ruleId,
        kind: r.kind,
        metric: r.metric,
        label: r.label,
        status: r.status,
        detail: r.detail,
        minimum: r.minimum ?? null,
        actual: r.actual ?? null,
        persons: r.persons,
      }));
      const lines = results
        .filter((r) => r.status === 'fail' || r.status === 'warning' || r.status === 'notReady')
        .map((r) => `${r.label}: ${r.detail || r.status}`);
      const summary = [
        c.fails ? `${c.fails} fail` : null,
        c.warnings ? `${c.warnings} warn` : null,
        c.notReady ? `${c.notReady} pending` : null,
      ]
        .filter(Boolean)
        .join(', ');
      return {
        status: c.status,
        summary: summary || 'All rules pass',
        lines,
        results,
      };
    },
    [],
  );

  const mergePoolMatrixBadges = useCallback(
    (
      candidates: {
        crewId: number;
        status: string;
        fails: number;
        warnings: number;
        notReady: number;
        results: MatrixRuleResult[];
      }[],
      outgoingTransactionId?: number | null,
    ) => {
      if (!candidates.length) return;
      setPoolMatrixByCrew((prev) => {
        const next = new Map(prev);
        for (const c of candidates) {
          next.set(c.crewId, buildMatrixBadge(c));
        }
        return next;
      });
      if (outgoingTransactionId != null) {
        setSlotMatrixByKey((prev) => {
          const next = new Map(prev);
          for (const c of candidates) {
            next.set(slotMatrixKey(outgoingTransactionId, c.crewId), buildMatrixBadge(c));
          }
          return next;
        });
      }
    },
    [buildMatrixBadge],
  );

  const runRankCandidatesForLine = useCallback(
    async (line: BoardLine) => {
      if (!applied?.vesselId || !line.replacement) return;
      setRankingPool(true);
      try {
        const crewIds = poolRows.map((r) => r.crewId);
        if (!crewIds.length) return;
        const res = await api.post<{
          candidates: {
            crewId: number;
            status: string;
            fails: number;
            warnings: number;
            notReady: number;
            results: MatrixRuleResult[];
          }[];
          hasMatrix?: boolean;
          matrix?: { id: number; name: string } | null;
        }>('/crew-manning-matrix/rank-candidates', {
          vesselId: Number(applied.vesselId),
          outgoingTransactionId: line.replacement.outgoingTransactionId,
          crewIds,
        });
        if (res.data?.hasMatrix === false) {
          setVesselHasMatrix(false);
          setVesselMatrixName(null);
          setPoolMatrixByCrew(new Map());
          return;
        }
        if (res.data?.hasMatrix) {
          setVesselHasMatrix(true);
          setVesselMatrixName(res.data.matrix?.name ?? null);
        }
        mergePoolMatrixBadges(res.data?.candidates ?? [], line.replacement.outgoingTransactionId);
      } catch {
        // Keep existing badges on pool-rank failure.
      } finally {
        setRankingPool(false);
        setRankSlotPick(null);
      }
    },
    [applied?.vesselId, poolRows, mergePoolMatrixBadges],
  );

  const handleRankCandidatesClick = useCallback(() => {
    if (!openSlotsForRank.length) return;
    if (openSlotsForRank.length === 1) {
      void runRankCandidatesForLine(openSlotsForRank[0]);
      return;
    }
    setRankSlotPick(openSlotsForRank);
  }, [openSlotsForRank, runRankCandidatesForLine]);

  // Clear matrix state when vessel changes.
  useEffect(() => {
    setPoolMatrixByCrew(new Map());
    setSlotMatrixByKey(new Map());
    setVesselHasMatrix(null);
    setVesselMatrixName(null);
  }, [applied?.vesselId]);

  // On search / board load, score all open slots in one request (slot-scoped).
  useEffect(() => {
    if (!applied?.vesselId || !boardMatrixScoreKey || !replData?.rows) return;
    const vesselIdNum = Number(applied.vesselId);
    if (!Number.isFinite(vesselIdNum)) return;

    let cancelled = false;
    const openRows = replData.rows.filter(
      (r) => r.status !== 'CANCELLED' && r.status !== 'COMPLETED',
    );
    const slots = openRows
      .map((row) => {
        const crewIds = Array.from(
          new Set(
            [row.incomingCrewId, ...(row.candidates ?? []).map((c) => c.crewId)].filter(
              (id): id is number => id != null && id > 0,
            ),
          ),
        );
        if (!crewIds.length || !row.outgoingTransactionId) return null;
        return {
          outgoingTransactionId: row.outgoingTransactionId,
          crewIds,
          asOf: row.plannedSignOnDate || row.plannedSignOffDate || undefined,
        };
      })
      .filter((s): s is NonNullable<typeof s> => s != null);

    (async () => {
      setScoringBoard(true);
      try {
        if (!slots.length) {
          // Still probe matrix presence with an empty score-board call.
          const res = await api.post<{
            hasMatrix: boolean;
            matrix: { id: number; name: string } | null;
            slots: {
              outgoingTransactionId: number;
              candidates: {
                crewId: number;
                status: string;
                fails: number;
                warnings: number;
                notReady: number;
                results: MatrixRuleResult[];
              }[];
            }[];
          }>('/crew-manning-matrix/score-board', {
            vesselId: vesselIdNum,
            slots: [],
          });
          if (cancelled) return;
          setVesselHasMatrix(Boolean(res.data?.hasMatrix));
          setVesselMatrixName(res.data?.matrix?.name ?? null);
          return;
        }

        const res = await api.post<{
          hasMatrix: boolean;
          matrix: { id: number; name: string } | null;
          slots: {
            outgoingTransactionId: number;
            candidates: {
              crewId: number;
              status: string;
              fails: number;
              warnings: number;
              notReady: number;
              results: MatrixRuleResult[];
            }[];
          }[];
        }>('/crew-manning-matrix/score-board', {
          vesselId: vesselIdNum,
          slots,
        });
        if (cancelled) return;
        setVesselHasMatrix(Boolean(res.data?.hasMatrix));
        setVesselMatrixName(res.data?.matrix?.name ?? null);
        if (!res.data?.hasMatrix) {
          setSlotMatrixByKey(new Map());
          return;
        }
        setSlotMatrixByKey(() => {
          const next = new Map<string, MatrixCrewBadge>();
          for (const slot of res.data?.slots ?? []) {
            for (const c of slot.candidates ?? []) {
              next.set(slotMatrixKey(slot.outgoingTransactionId, c.crewId), buildMatrixBadge(c));
            }
          }
          return next;
        });
      } catch {
        if (!cancelled) {
          setVesselHasMatrix(null);
        }
      } finally {
        if (!cancelled) setScoringBoard(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [applied?.vesselId, boardMatrixScoreKey, replData?.rows, buildMatrixBadge]);

  const addPossible = useCallback(
    async (line: BoardLine, crewId: number) => {
      setBusyKey(line.key);
      try {
        if (line.replacement) {
          await addCrewReplacementCandidate(line.replacement.id, { crewId });
        } else {
          await addCrewReplacementCandidateForTour({
            outgoingTransactionId: line.onboard.transactionId,
            crewId,
          });
        }
        await mutateRepl();
      } finally {
        setBusyKey(null);
      }
    },
    [mutateRepl],
  );

  const assignIncoming = useCallback(
    async (line: BoardLine, incomingCrewId: number) => {
      setBusyKey(line.key);
      try {
        // Move: clear this relief from any other open slot first.
        const others = (replData?.rows ?? []).filter(
          (r) =>
            r.incomingCrewId === incomingCrewId &&
            r.outgoingTransactionId !== line.onboard.transactionId &&
            r.status !== 'CANCELLED' &&
            r.status !== 'COMPLETED',
        );
        for (const other of others) {
          await updateCrewReplacement(other.id, { incomingCrewId: null, status: 'NEEDED' });
        }

        // Ensure they are also tracked as a possible.
        if (line.replacement) {
          await addCrewReplacementCandidate(line.replacement.id, { crewId: incomingCrewId });
          await updateCrewReplacement(line.replacement.id, {
            incomingCrewId,
            status:
              line.replacement.status === 'NEEDED' || line.replacement.status === 'CANCELLED'
                ? 'CANDIDATE'
                : line.replacement.status,
          });
        } else {
          const created = await addCrewReplacementCandidateForTour({
            outgoingTransactionId: line.onboard.transactionId,
            crewId: incomingCrewId,
          });
          const replacementId = (created as { id?: number })?.id;
          if (replacementId) {
            await updateCrewReplacement(replacementId, {
              incomingCrewId,
              status: 'CANDIDATE',
            });
          }
        }
        await mutateRepl();
      } finally {
        setBusyKey(null);
      }
    },
    [mutateRepl, replData?.rows],
  );

  const requestAddPossible = useCallback(
    (line: BoardLine, incomingCrewId: number) => {
      const relief = reliefById.get(incomingCrewId);
      const matrix = lookupSlotOrPoolMatrix(
        line.replacement?.outgoingTransactionId ?? line.onboard.transactionId,
        incomingCrewId,
        slotMatrixByKey,
        poolMatrixByCrew,
      );
      const warnings = relief
        ? getAssignWarnings(line.rankId, line.rankLabel, relief, undefined, matrix?.lines)
        : [];
      if (warnings.length) {
        setPendingAssign({
          line,
          crewId: incomingCrewId,
          name: relief?.name || `Crew #${incomingCrewId}`,
          warnings,
          mode: 'possible',
        });
        return;
      }
      void addPossible(line, incomingCrewId);
    },
    [reliefById, addPossible, slotMatrixByKey, poolMatrixByCrew],
  );

  const requestAssign = useCallback(
    (line: BoardLine, incomingCrewId: number) => {
      const relief = reliefById.get(incomingCrewId);
      const matrix = lookupSlotOrPoolMatrix(
        line.replacement?.outgoingTransactionId ?? line.onboard.transactionId,
        incomingCrewId,
        slotMatrixByKey,
        poolMatrixByCrew,
      );
      const warnings = relief
        ? getAssignWarnings(line.rankId, line.rankLabel, relief, undefined, matrix?.lines)
        : [];
      if (warnings.length) {
        setPendingAssign({
          line,
          crewId: incomingCrewId,
          name: relief?.name || `Crew #${incomingCrewId}`,
          warnings,
          mode: 'select',
        });
        return;
      }
      void assignIncoming(line, incomingCrewId);
    },
    [reliefById, assignIncoming, slotMatrixByKey, poolMatrixByCrew],
  );

  const selectCandidate = useCallback(
    async (row: CrewReplacementRow, candidate: CrewReplacementCandidateRow) => {
      setBusyKey(`repl-${row.id}`);
      try {
        const others = (replData?.rows ?? []).filter(
          (r) =>
            r.incomingCrewId === candidate.crewId &&
            r.id !== row.id &&
            r.status !== 'CANCELLED' &&
            r.status !== 'COMPLETED',
        );
        for (const other of others) {
          await updateCrewReplacement(other.id, { incomingCrewId: null, status: 'NEEDED' });
        }
        await selectCrewReplacementCandidate(row.id, candidate.id);
        await mutateRepl();
      } finally {
        setBusyKey(null);
      }
    },
    [mutateRepl, replData?.rows],
  );

  const removeCandidate = useCallback(
    async (row: CrewReplacementRow, candidate: CrewReplacementCandidateRow) => {
      setBusyKey(`repl-${row.id}`);
      try {
        await removeCrewReplacementCandidate(row.id, candidate.id);
        await mutateRepl();
      } finally {
        setBusyKey(null);
      }
    },
    [mutateRepl],
  );

  const clearIncoming = useCallback(
    async (line: BoardLine) => {
      if (!line.replacement) return;
      setBusyKey(line.key);
      try {
        await updateCrewReplacement(line.replacement.id, {
          incomingCrewId: null,
          status: 'NEEDED',
        });
        setClearTarget(null);
        await mutateRepl();
      } finally {
        setBusyKey(null);
      }
    },
    [mutateRepl],
  );

  const handleOpenTickets = useCallback(
    async (row: CrewReplacementRow) => {
      setWorkflowBusy(true);
      try {
        const travel = (await createCrewTravelFromReplacement(row.id)) as { id: number };
        navigate(`/crew/crew-changes-cost/${travel.id}/edit`);
      } catch (err) {
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Could not open Crew Changes Cost';
        setSeedMsg(String(msg));
      } finally {
        setWorkflowBusy(false);
      }
    },
    [navigate],
  );

  const handleAdvance = useCallback(
    async (row: CrewReplacementRow, status: CrewReplacementStatus) => {
      if (row.status === 'COMPLETED' && status === 'TRAVEL') {
        setUndoCompleteTarget(row);
        return;
      }
      setWorkflowBusy(true);
      try {
        await advanceCrewReplacement(row.id, status);
        await Promise.all([mutateRepl(), mutateVessel()]);
      } catch (err) {
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          'Could not advance status';
        setSeedMsg(String(msg));
      } finally {
        setWorkflowBusy(false);
      }
    },
    [mutateRepl, mutateVessel],
  );

  const handleUndoCompleteConfirm = useCallback(async () => {
    if (!undoCompleteTarget) return;
    setWorkflowBusy(true);
    try {
      await advanceCrewReplacement(undoCompleteTarget.id, 'TRAVEL');
      setUndoCompleteTarget(null);
      await Promise.all([mutateRepl(), mutateVessel()]);
      setSeedMsg('Reverted S/Off + S/On — status back to Travel.');
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Could not undo completion';
      setSeedMsg(String(msg));
    } finally {
      setWorkflowBusy(false);
    }
  }, [undoCompleteTarget, mutateRepl, mutateVessel]);

  const handleCompleteConfirm = useCallback(async () => {
    if (!completeTarget) return;
    setWorkflowBusy(true);
    try {
      await completeCrewReplacement(completeTarget.id);
      setCompleteTarget(null);
      await Promise.all([mutateRepl(), mutateVessel()]);
      setSeedMsg('Completed: outgoing S/Off + incoming S/On created.');
    } catch (err) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Could not complete replacement';
      setSeedMsg(String(msg));
    } finally {
      setWorkflowBusy(false);
    }
  }, [completeTarget, mutateRepl, mutateVessel]);

  const handleSeed = useCallback(async () => {
    if (!applied?.vesselId) return;
    setSeeding(true);
    setSeedMsg(null);
    try {
      const result = await seedCrewReplacements({
        vesselId: Number(applied.vesselId),
        withinDays: Number(applied.withinDays) || 90,
      });
      setSeedMsg(
        `Seeded ${result.created} new · revived ${result.revived} · skipped ${result.skipped}`,
      );
      await mutateRepl();
    } catch {
      setSeedMsg('Could not seed from COE.');
    } finally {
      setSeeding(false);
    }
  }, [applied, mutateRepl]);

  const openEdit = useCallback((row: CrewReplacementRow) => {
    setEditRow(row);
    setEditForm({
      plannedSignOffDate: row.plannedSignOffDate || '',
      plannedSignOnDate: row.plannedSignOnDate || '',
      changePortId: row.changePortId != null ? String(row.changePortId) : '',
      status: row.status,
      overlapDays: row.overlapDays != null ? String(row.overlapDays) : '',
      notes: row.notes || '',
    });
  }, []);

  const saveEdit = useCallback(async () => {
    if (!editRow) return;
    setSaving(true);
    try {
      await updateCrewReplacement(editRow.id, {
        plannedSignOffDate: editForm.plannedSignOffDate || null,
        plannedSignOnDate: editForm.plannedSignOnDate || null,
        changePortId: editForm.changePortId ? Number(editForm.changePortId) : null,
        status: editForm.status,
        overlapDays: editForm.overlapDays ? Number(editForm.overlapDays) : null,
        notes: editForm.notes || null,
      });
      setEditRow(null);
      await mutateRepl();
    } finally {
      setSaving(false);
    }
  }, [editRow, editForm, mutateRepl]);

  /** Keep pick modal in sync after adding candidates (mutate refreshes allLines). */
  const livePickLine = useMemo(() => {
    if (!pickLine) return null;
    return allLines.find((l) => l.key === pickLine.key) ?? pickLine;
  }, [pickLine, allLines]);

  const pickOptions = useMemo(() => {
    const rows = relief?.rows ?? [];
    const withoutSelf = rows.filter((r) => r.crewId !== livePickLine?.onboard.crewId);
    if (livePickLine?.rankId == null) return withoutSelf;
    const sameRank = withoutSelf.filter((r) => r.rankId === livePickLine.rankId);
    return sameRank.length ? sameRank : withoutSelf;
  }, [relief?.rows, livePickLine]);

  const rankFilterOptions = useMemo(() => {
    const ranksById = new Map(
      (lookups?.ranks ?? []).map((r) => [r.id, r] as const),
    );
    const present = new Map<number, { label: string; count: number }>();
    for (const row of relief?.rows ?? []) {
      if (row.rankId == null) continue;
      const existing = present.get(row.rankId);
      if (existing) {
        existing.count += 1;
        continue;
      }
      const lookup = ranksById.get(row.rankId);
      present.set(row.rankId, {
        label: lookup?.abrv || lookup?.name || row.rank || row.lastRank || String(row.rankId),
        count: 1,
      });
    }
    const total = relief?.rows?.length ?? 0;
    const options = Array.from(present.entries())
      .map(([id, { label, count }]) => {
        const sortOrder = ranksById.get(id)?.sortOrder ?? 9999;
        return {
          value: String(id),
          label,
          sortOrder,
          count,
          trailing: (
            <CountChip className="min-w-[1.5rem] justify-center px-1.5 py-0 text-[10px]">
              {count}
            </CountChip>
          ),
        };
      })
      .sort((a, b) => {
        if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
        return a.label.localeCompare(b.label);
      })
      .map(({ value, label, trailing }) => ({ value, label, trailing }));
    return [
      {
        value: '',
        label: 'All ranks',
        trailing: (
          <CountChip className="min-w-[1.5rem] justify-center px-1.5 py-0 text-[10px]">
            {total}
          </CountChip>
        ),
      },
      ...options,
    ];
  }, [lookups?.ranks, relief?.rows]);

  useEffect(() => {
    if (!rankFilter) return;
    const stillPresent = rankFilterOptions.some((o) => o.value === rankFilter);
    if (!stillPresent) setRankFilter('');
  }, [rankFilter, rankFilterOptions]);

  const poolRankLabel = useMemo(() => {
    if (!rankFilter) return null;
    return rankFilterOptions.find((o) => o.value === rankFilter)?.label ?? null;
  }, [rankFilter, rankFilterOptions]);

  const openMatrixDetail = useCallback(
    (partial: {
      crewName: string;
      badge: MatrixCrewBadge;
      rankLabel?: string | null;
      asOf?: string | null;
    }) => {
      setMatrixDetail({
        crewName: partial.crewName,
        badge: partial.badge,
        rankLabel: partial.rankLabel ?? poolRankLabel,
        asOf: partial.asOf ?? null,
        vesselName: vesselList?.vessel?.vesselName ?? null,
        noMatrix: vesselHasMatrix === false,
      });
    },
    [poolRankLabel, vesselList?.vessel?.vesselName, vesselHasMatrix],
  );

  return (
    <div className="h-full flex flex-col min-h-0 gap-3">
      <CrewReportSearchHeader
        hideBack
        hint={{
          title: 'Crew Replacement Planner',
          body: 'Ranks on the left, onboard crew for the selected vessel, then a replacement slot. Drag a shore candidate from the right onto a slot, or use Replace.',
        }}
        onSearch={handleSearch}
        searchDisabled={!vesselId}
        loading={vesselLoading}
        onRefresh={applied ? () => void refresh() : undefined}
        context={
          vesselList?.vessel && applied
            ? `${vesselList.vessel.vesselName} · ${lines.length} onboard · drag replacement onto a slot`
            : undefined
        }
        trailingActions={
          applied ? (
            <>
              <IconBtn
                title="Audit history"
                onClick={() =>
                  setHistoryTarget({
                    mode: 'vessel',
                    title: vesselList?.vessel?.vesselName
                      ? `History · ${vesselList.vessel.vesselName}`
                      : 'Vessel replacement history',
                  })
                }
                className={cn('h-10 w-10 border', colors.border)}
              >
                <ClockIcon className="h-5 w-5" />
              </IconBtn>
              <Link
                to={replacementPlanPrintPath(applied)}
                title="Print replacement plan"
                className={cn(
                  'inline-flex h-10 w-10 items-center justify-center rounded-md border transition-colors',
                  colors.border,
                  colors.text,
                  'hover:bg-black/5 dark:hover:bg-white/10',
                )}
              >
                <PrinterIcon className="h-5 w-5" />
              </Link>
              <IconBtn
                title={seeding ? 'Seeding from COE…' : 'Seed from COE'}
                disabled={seeding}
                onClick={() => void handleSeed()}
                className={cn('h-10 w-10 border', colors.border)}
              >
                <UserPlusIcon className="h-5 w-5" />
              </IconBtn>
            </>
          ) : null
        }
      >
        <CrewReportFilterField label="Vessel" htmlFor="repl-vessel" width="lg">
          <VesselSelect
            name="vesselId"
            value={vesselId}
            onChange={(e) => setVesselId(String(e.target.value))}
            placeholder="Select vessel…"
            includeIds={vesselId ? [vesselId] : undefined}
          />
        </CrewReportFilterField>
        <CrewReportFilterField label="Within days" htmlFor="repl-within" width="md">
          <Select
            name="withinDays"
            value={withinDays}
            onChange={(e) => setWithinDays(String(e.target.value))}
            options={WITHIN_DAYS_OPTIONS}
          />
        </CrewReportFilterField>
        <CrewReportFilterField label="Rows" htmlFor="repl-needs" width="md">
          <Select
            name="needsReliefOnly"
            value={needsReliefOnly ? 'needs' : 'all'}
            onChange={(e) => setNeedsReliefOnly(e.target.value === 'needs')}
            options={[
              { value: 'needs', label: 'Needs relief' },
              { value: 'all', label: 'All onboard' },
            ]}
          />
        </CrewReportFilterField>
      </CrewReportSearchHeader>

      {seedMsg ? (
        <div className={cn('text-sm px-1', isDark ? 'text-sky-300' : 'text-sky-700')}>{seedMsg}</div>
      ) : null}

      {!applied ? (
        <CrewDataTablePrompt>Select a vessel and search to plan replacements.</CrewDataTablePrompt>
      ) : vesselError ? (
        <CrewDataTablePrompt>
          <span className={isDark ? 'text-red-400' : 'text-red-600'}>Could not load vessel crew.</span>
        </CrewDataTablePrompt>
      ) : (
        <div
          ref={splitRef}
          className={cn(
            'flex flex-1 min-h-0 flex-col xl:flex-row',
            panelResizing && 'select-none',
          )}
        >
          <div
            className={cn(
              'min-h-0 min-w-0 flex-1 overflow-auto rounded-xl border',
              colors.surface,
              colors.border,
            )}
          >
            <div
              className={cn(
                'sticky top-0 z-10 grid grid-cols-[5.5rem_minmax(0,1fr)_minmax(18rem,1.35fr)] gap-3 border-b px-3 py-2 text-xs font-semibold uppercase tracking-wide',
                colors.border,
                isDark ? 'bg-zinc-900' : 'bg-slate-50',
                colors.textSecondary,
              )}
            >
              <div>Rank</div>
              <div>Onboard</div>
              <div className="flex items-center gap-2 normal-case tracking-normal">
                <span className="text-xs font-semibold uppercase tracking-wide">Replacement</span>
                {scoringBoard ? (
                  <span className={cn('text-[10px] font-normal normal-case', colors.textMuted)}>
                    Scoring matrix…
                  </span>
                ) : vesselHasMatrix === false ? (
                  <span
                    className={cn(
                      'rounded px-1.5 py-0.5 text-[10px] font-medium normal-case',
                      isDark
                        ? 'bg-amber-500/15 text-amber-300'
                        : 'bg-amber-500/15 text-amber-800',
                    )}
                  >
                    No matrix for this vessel
                  </span>
                ) : vesselHasMatrix && vesselMatrixName ? (
                  <span className={cn('text-[10px] font-normal normal-case truncate', colors.textMuted)}>
                    {vesselMatrixName}
                  </span>
                ) : null}
              </div>
            </div>

            {vesselLoading ? (
              <div className={cn('px-4 py-8 text-sm', colors.textSecondary)}>Loading…</div>
            ) : lines.length === 0 ? (
              <div className={cn('px-4 py-8 text-sm', colors.textSecondary)}>
                No onboard crew for this vessel today.
              </div>
            ) : (
              <div>
                {lines.map((line) => (
                  <div
                    key={line.key}
                    className={cn(
                      'grid grid-cols-[5.5rem_minmax(0,1fr)_minmax(18rem,1.35fr)] gap-3 px-3 py-1.5 items-center border-b',
                      colors.border,
                    )}
                  >
                    <div className={cn('text-sm font-semibold truncate', colors.text)}>
                      {line.showRankLabel ? line.rankLabel : ''}
                    </div>
                    <OnboardPersonCell
                      row={line.onboard}
                      notes={line.replacement?.notes}
                      withinDays={Number(applied.withinDays) || 90}
                      from={listReturnPath}
                    />
                    <ReplacementSlot
                      line={line}
                      busy={busyKey === line.key || workflowBusy}
                      incomingProfile={
                        line.replacement?.incomingCrewId
                          ? reliefById.get(line.replacement.incomingCrewId) ?? null
                          : null
                      }
                      reliefById={reliefById}
                      lookupMatrix={(crewId) =>
                        lookupSlotOrPoolMatrix(
                          line.replacement?.outgoingTransactionId ??
                            line.onboard.transactionId,
                          crewId,
                          slotMatrixByKey,
                          poolMatrixByCrew,
                        )
                      }
                      onOpenMatrix={(crewName, badge, ctx) =>
                        openMatrixDetail({
                          crewName,
                          badge,
                          rankLabel: ctx?.rankLabel,
                          asOf: ctx?.asOf,
                        })
                      }
                      conflictDuplicate={
                        line.replacement ? conflicts.duplicateRelief.has(line.replacement.id) : false
                      }
                      conflictOverlap={
                        line.replacement ? conflicts.overlapping.has(line.replacement.id) : false
                      }
                      onDropCrew={(l, crewId) => requestAddPossible(l, crewId)}
                      onOpenPick={(l) => {
                        if (l.rankId != null) setRankFilter(String(l.rankId));
                        setPickLine(l);
                      }}
                      onOpenEdit={openEdit}
                      onOpenHistory={(row) =>
                        setHistoryTarget({
                          mode: 'slot',
                          replacementId: row.id,
                          title: `History · ${row.outgoingCrewName}${row.rank ? ` (${row.rank})` : ''}`,
                        })
                      }
                      onClear={(l) => setClearTarget(l)}
                      onAdvance={(row, status) => void handleAdvance(row, status)}
                      onComplete={(row) => setCompleteTarget(row)}
                      onTickets={(row) => void handleOpenTickets(row)}
                      onSelectCandidate={(row, c) => {
                        const profile = reliefById.get(c.crewId);
                        const matrix = lookupSlotOrPoolMatrix(
                          row.outgoingTransactionId,
                          c.crewId,
                          slotMatrixByKey,
                          poolMatrixByCrew,
                        );
                        const warnings = profile
                          ? getAssignWarnings(
                              row.rankId,
                              row.rank || 'slot',
                              profile,
                              undefined,
                              matrix?.lines,
                            )
                          : [];
                        setPendingSelectCandidate({ row, candidate: c, warnings });
                      }}
                      onRemoveCandidate={(row, c) => setPendingRemoveCandidate({ row, candidate: c })}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="hidden xl:flex">
            <ReferenceSidebarSplitter
              panelWidth={panelWidth}
              minWidth={minWidth}
              maxWidth={maxWidth}
              resizing={panelResizing}
              onMouseDown={startResize}
              label="Resize relief pool"
            />
          </div>

          <aside
            style={{ ['--repl-pool-w' as string]: `${panelWidth}px` }}
            className={cn(
              'mt-3 flex min-h-[16rem] w-full min-w-0 flex-col overflow-hidden rounded-xl border xl:mt-0 xl:w-[var(--repl-pool-w)] xl:shrink-0 xl:min-h-0',
              colors.surface,
              colors.border,
            )}
          >
            <div className={cn('border-b px-3 py-2 space-y-2', colors.border)}>
              <div className="flex items-center justify-between gap-2">
                <div className={cn('text-sm font-medium', colors.text)}>Relief pool</div>
                <div className="flex items-center gap-0.5">
                  <Hint content="Expand all" side="top" delay={180} triggerClassName="inline-flex">
                    <button
                      type="button"
                      onClick={expandPoolAll}
                      disabled={poolRows.length === 0}
                      className={cn(
                        'inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors',
                        colors.textMuted,
                        colors.interactiveHover,
                        'disabled:pointer-events-none disabled:opacity-40',
                      )}
                      aria-label="Expand all"
                    >
                      <ExpandAllIcon className="h-3.5 w-3.5" />
                    </button>
                  </Hint>
                  <Hint content="Collapse all" side="top" delay={180} triggerClassName="inline-flex">
                    <button
                      type="button"
                      onClick={collapsePoolAll}
                      disabled={poolRows.length === 0}
                      className={cn(
                        'inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors',
                        colors.textMuted,
                        colors.interactiveHover,
                        'disabled:pointer-events-none disabled:opacity-40',
                      )}
                      aria-label="Collapse all"
                    >
                      <CollapseAllIcon className="h-3.5 w-3.5" />
                    </button>
                  </Hint>
                </div>
              </div>
              <p className={cn('text-xs', colors.textSecondary)}>Drag a name onto a replacement slot.</p>
              <Select
                value={rankFilter}
                onChange={(e) => setRankFilter(String(e.target.value))}
                options={rankFilterOptions}
                placeholder="Filter by rank"
                searchPlaceholder="Search ranks…"
                size="sm"
                showClearButton={Boolean(rankFilter)}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full"
                disabled={
                  !applied?.vesselId ||
                  !rankFilter ||
                  openSlotsForRank.length === 0 ||
                  poolRows.length === 0 ||
                  rankingPool ||
                  vesselHasMatrix === false
                }
                onClick={handleRankCandidatesClick}
              >
                {rankingPool
                  ? 'Ranking…'
                  : vesselHasMatrix === false
                    ? 'No matrix configured'
                    : 'Rank candidates'}
              </Button>
            </div>
            <div className="flex-1 min-h-0 overflow-auto p-2">
              {poolRows.length === 0 ? (
                <div className={cn('text-xs px-1 py-4', colors.textSecondary)}>
                  No shore candidates for this filter.
                </div>
              ) : (
                <Accordion className="w-full space-y-2">
                  {poolRows.map((row) => (
                    <ReliefCard
                      key={row.crewId}
                      row={row}
                      open={poolOpenIds.has(row.crewId)}
                      onToggle={(open) => togglePoolOpen(row.crewId, open)}
                      matrixBadge={
                        vesselHasMatrix === false
                          ? null
                          : poolMatrixByCrew.get(row.crewId) ?? null
                      }
                      onOpenMatrix={(badge) =>
                        openMatrixDetail({ crewName: row.name, badge })
                      }
                    />
                  ))}
                </Accordion>
              )}
            </div>
          </aside>
        </div>
      )}

      <MatrixRulesDetailModal
        open={!!matrixDetail}
        detail={matrixDetail}
        fromPath={listReturnPath}
        onClose={() => setMatrixDetail(null)}
      />

      <Modal isOpen={!!rankSlotPick} onClose={() => setRankSlotPick(null)} size="md">
        <ModalHeader>
          <ModalTitle>Which relief slot?</ModalTitle>
        </ModalHeader>
        <ModalContent className="space-y-2">
          <p className={cn('text-xs', colors.textSecondary)}>
            More than one open slot uses this rank. Choose who is signing off so the pool is scored
            against that change.
          </p>
          {(rankSlotPick ?? []).map((line) => (
            <button
              key={line.key}
              type="button"
              className={cn(
                'flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm',
                colors.border,
                colors.interactiveHover,
              )}
              onClick={() => void runRankCandidatesForLine(line)}
            >
              <span className={colors.text}>{crewName(line.onboard)}</span>
              <span className={cn('text-xs', colors.textSecondary)}>{line.rankLabel}</span>
            </button>
          ))}
        </ModalContent>
        <ModalFooter>
          <IconBtn title="Close" tone="muted" onClick={() => setRankSlotPick(null)}>
            <XIcon className="h-5 w-5" />
          </IconBtn>
        </ModalFooter>
      </Modal>

      <Modal isOpen={!!livePickLine} onClose={() => setPickLine(null)} size="lg">
        <ModalHeader>
          <ModalTitle>
            {livePickLine
              ? `Possible replacements for ${crewName(livePickLine.onboard)} (${livePickLine.rankLabel})`
              : 'Possible replacements'}
          </ModalTitle>
        </ModalHeader>
        <ModalContent className="max-h-[60vh] overflow-auto">
          <p className={cn('mb-3 text-xs', colors.textSecondary)}>
            Add several people you are speaking with. Select one as the relief when ready.
          </p>
          {pickOptions.length === 0 ? (
            <p className={cn('text-sm', colors.textSecondary)}>No relief candidates available.</p>
          ) : (
            <Accordion className="space-y-2">
              {pickOptions.map((row) => {
                const already =
                  livePickLine?.replacement?.candidates?.some((c) => c.crewId === row.crewId) ??
                  false;
                const badge =
                  vesselHasMatrix === false
                    ? null
                    : lookupSlotOrPoolMatrix(
                        livePickLine?.replacement?.outgoingTransactionId ??
                          livePickLine?.onboard.transactionId,
                        row.crewId,
                        slotMatrixByKey,
                        poolMatrixByCrew,
                      );
                return (
                  <AccordionItem
                    key={row.crewId}
                    title={row.name}
                    description={
                      [
                        reliefSummary(row) || 'Shore candidate',
                        badge?.summary || null,
                        already ? 'Already listed' : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')
                    }
                    defaultOpen={false}
                    headerClassName="px-3 py-2"
                    headerAction={
                      <div className="flex items-center gap-1">
                        {badge ? (
                          <MatrixStatusBadge
                            badge={badge}
                            className="mr-1"
                            onOpen={() =>
                              openMatrixDetail({
                                crewName: row.name,
                                badge,
                                rankLabel: livePickLine?.rankLabel,
                                asOf:
                                  livePickLine?.replacement?.plannedSignOnDate ||
                                  livePickLine?.replacement?.plannedSignOffDate ||
                                  null,
                              })
                            }
                          />
                        ) : null}
                        <IconBtn
                          title={`Add ${row.name} as possible`}
                          disabled={already}
                          onClick={() => {
                            if (!livePickLine) return;
                            void requestAddPossible(livePickLine, row.crewId);
                          }}
                        >
                          <UserPlusIcon className="h-5 w-5" />
                        </IconBtn>
                        <IconBtn
                          title={`Select ${row.name} as relief`}
                          onClick={() => {
                            if (!livePickLine) return;
                            const line = livePickLine;
                            setPickLine(null);
                            requestAssign(line, row.crewId);
                          }}
                        >
                          <CheckIcon className="h-5 w-5" />
                        </IconBtn>
                      </div>
                    }
                  >
                    <IncomingProfile row={row} />
                    {badge ? (
                      <button
                        type="button"
                        className={cn(
                          'mt-2 text-left text-[11px] underline-offset-2 hover:underline',
                          colors.textSecondary,
                        )}
                        onClick={() =>
                          openMatrixDetail({
                            crewName: row.name,
                            badge,
                            rankLabel: livePickLine?.rankLabel,
                            asOf:
                              livePickLine?.replacement?.plannedSignOnDate ||
                              livePickLine?.replacement?.plannedSignOffDate ||
                              null,
                          })
                        }
                      >
                        View matrix rules (required vs have)
                      </button>
                    ) : null}
                  </AccordionItem>
                );
              })}
            </Accordion>
          )}
        </ModalContent>
        <ModalFooter>
          <IconBtn title="Close" tone="muted" onClick={() => setPickLine(null)}>
            <XIcon className="h-5 w-5" />
          </IconBtn>
        </ModalFooter>
      </Modal>

      <Modal isOpen={!!editRow} onClose={() => setEditRow(null)} size="lg">
        <ModalHeader>
          <ModalTitle>
            {editRow ? `Replacement · ${editRow.outgoingCrewName}` : 'Edit replacement'}
          </ModalTitle>
        </ModalHeader>
        <ModalContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <CrewReportFilterField label="Status" width="fill">
              <Select
                value={editForm.status}
                onChange={(e) =>
                  setEditForm((f) => ({
                    ...f,
                    status: String(e.target.value) as CrewReplacementStatus,
                  }))
                }
                options={EDIT_STATUS_OPTIONS}
              />
            </CrewReportFilterField>
            <CrewReportFilterField label="Change port" width="fill">
              <PortSelect
                value={editForm.changePortId}
                onChange={(e) => setEditForm((f) => ({ ...f, changePortId: String(e.target.value) }))}
                placeholder="—"
                includeIds={editForm.changePortId ? [editForm.changePortId] : undefined}
              />
            </CrewReportFilterField>
            <CrewReportFilterField label="Planned S/Off" width="fill">
              <DateInput
                value={editForm.plannedSignOffDate}
                onChange={(e) => setEditForm((f) => ({ ...f, plannedSignOffDate: e.target.value }))}
              />
            </CrewReportFilterField>
            <CrewReportFilterField label="Planned S/On" width="fill">
              <DateInput
                value={editForm.plannedSignOnDate}
                onChange={(e) => setEditForm((f) => ({ ...f, plannedSignOnDate: e.target.value }))}
              />
            </CrewReportFilterField>
            <CrewReportFilterField label="Overlap days" width="fill">
              <Select
                value={editForm.overlapDays}
                onChange={(e) => setEditForm((f) => ({ ...f, overlapDays: String(e.target.value) }))}
                options={[
                  { value: '', label: '—' },
                  ...[0, 1, 2, 3, 5, 7, 10, 14].map((n) => ({ value: String(n), label: String(n) })),
                ]}
              />
            </CrewReportFilterField>
          </div>
          <CrewReportFilterField label="Notes" width="fill">
            <Textarea
              value={editForm.notes}
              onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
              rows={3}
            />
          </CrewReportFilterField>
        </ModalContent>
        <ModalFooter>
          <IconBtn
            title="History"
            tone="muted"
            onClick={() => {
              if (!editRow) return;
              const row = editRow;
              setEditRow(null);
              setHistoryTarget({
                mode: 'slot',
                replacementId: row.id,
                title: `History · ${row.outgoingCrewName}${row.rank ? ` (${row.rank})` : ''}`,
              });
            }}
          >
            <ClockIcon className="h-5 w-5" />
          </IconBtn>
          <IconBtn title="Close" tone="muted" onClick={() => setEditRow(null)}>
            <XIcon className="h-5 w-5" />
          </IconBtn>
          <IconBtn title={saving ? 'Saving…' : 'Save'} disabled={saving} onClick={() => void saveEdit()}>
            <SaveIcon className="h-5 w-5" />
          </IconBtn>
        </ModalFooter>
      </Modal>

      <ConfirmationDialog
        isOpen={!!pendingAssign}
        onClose={() => setPendingAssign(null)}
        onConfirm={() => {
          if (!pendingAssign) return;
          const { line, crewId, mode } = pendingAssign;
          setPendingAssign(null);
          if (mode === 'possible') void addPossible(line, crewId);
          else void assignIncoming(line, crewId);
        }}
        title={pendingAssign?.mode === 'possible' ? 'Add possible with warnings?' : 'Select relief with warnings?'}
        message={
          pendingAssign
            ? pendingAssign.mode === 'possible'
              ? `Add ${pendingAssign.name} as a possible replacement despite the following:`
              : `Select ${pendingAssign.name} as relief despite the following:`
            : undefined
        }
        warningMessage={pendingAssign?.warnings.join('\n') || null}
        confirmText={pendingAssign?.mode === 'possible' ? 'Add anyway' : 'Select anyway'}
        confirmVariant="warning"
      />

      <ConfirmationDialog
        isOpen={!!clearTarget}
        onClose={() => setClearTarget(null)}
        onConfirm={() => {
          if (!clearTarget) return;
          void clearIncoming(clearTarget);
        }}
        title="Clear replacement?"
        message={
          clearTarget?.replacement
            ? `Remove ${clearTarget.replacement.incomingCrewName || 'the assigned relief'} from ${crewName(clearTarget.onboard)}? The slot will go back to Needed.`
            : undefined
        }
        confirmText={busyKey === clearTarget?.key ? 'Clearing…' : 'Clear'}
        confirmVariant="danger"
        isLoading={busyKey === clearTarget?.key}
      />

      <ConfirmationDialog
        isOpen={!!pendingSelectCandidate}
        onClose={() => setPendingSelectCandidate(null)}
        onConfirm={() => {
          if (!pendingSelectCandidate) return;
          const { row, candidate } = pendingSelectCandidate;
          setPendingSelectCandidate(null);
          void selectCandidate(row, candidate);
        }}
        title="Select as relief?"
        message={
          pendingSelectCandidate
            ? `Use ${pendingSelectCandidate.candidate.crewName} as the selected relief for ${pendingSelectCandidate.row.outgoingCrewName}? All other possibles will be cleared.`
            : undefined
        }
        warningMessage={
          pendingSelectCandidate?.warnings.length
            ? pendingSelectCandidate.warnings.join('\n')
            : null
        }
        confirmText={
          busyKey === `repl-${pendingSelectCandidate?.row.id}`
            ? 'Selecting…'
            : pendingSelectCandidate?.warnings.length
              ? 'Select anyway'
              : 'Select'
        }
        confirmVariant={pendingSelectCandidate?.warnings.length ? 'warning' : 'default'}
        isLoading={busyKey === `repl-${pendingSelectCandidate?.row.id}`}
      />

      <ConfirmationDialog
        isOpen={!!pendingRemoveCandidate}
        onClose={() => setPendingRemoveCandidate(null)}
        onConfirm={() => {
          if (!pendingRemoveCandidate) return;
          const { row, candidate } = pendingRemoveCandidate;
          setPendingRemoveCandidate(null);
          void removeCandidate(row, candidate);
        }}
        title="Remove possible replacement?"
        message={
          pendingRemoveCandidate
            ? pendingRemoveCandidate.candidate.isSelected
              ? `Remove ${pendingRemoveCandidate.candidate.crewName} from possibles? They are currently the selected relief and will be cleared.`
              : `Remove ${pendingRemoveCandidate.candidate.crewName} from the possible replacements list?`
            : undefined
        }
        confirmText={
          busyKey === `repl-${pendingRemoveCandidate?.row.id}` ? 'Removing…' : 'Remove'
        }
        confirmVariant="danger"
        isLoading={busyKey === `repl-${pendingRemoveCandidate?.row.id}`}
      />

      <ConfirmationDialog
        isOpen={!!completeTarget}
        onClose={() => setCompleteTarget(null)}
        onConfirm={() => void handleCompleteConfirm()}
        title="Complete crew change?"
        message={
          completeTarget
            ? `This will sign off ${completeTarget.outgoingCrewName} and sign on ${completeTarget.incomingCrewName || 'relief'} on the vessel (real transactions).`
            : undefined
        }
        confirmText={workflowBusy ? 'Working…' : 'Complete'}
        confirmVariant="danger"
        isLoading={workflowBusy}
      />

      <ConfirmationDialog
        isOpen={!!undoCompleteTarget}
        onClose={() => setUndoCompleteTarget(null)}
        onConfirm={() => void handleUndoCompleteConfirm()}
        title="Undo S/Off + S/On?"
        message={
          undoCompleteTarget
            ? `This restores ${undoCompleteTarget.outgoingCrewName} as ONB and removes the relief ONB tour for ${undoCompleteTarget.incomingCrewName || 'the incoming crew'}.`
            : undefined
        }
        confirmText={workflowBusy ? 'Working…' : 'Undo'}
        confirmVariant="warning"
        isLoading={workflowBusy}
      />

      <Modal isOpen={!!historyTarget} onClose={() => setHistoryTarget(null)} size="lg">
        <ModalHeader>
          <ModalTitle>{historyTarget?.title || 'History'}</ModalTitle>
        </ModalHeader>
        <ModalContent className="max-h-[65vh] overflow-auto">
          {historyLoading ? (
            <p className={cn('text-sm', colors.textSecondary)}>Loading history…</p>
          ) : historyError ? (
            <p className={cn('text-sm', isDark ? 'text-red-400' : 'text-red-600')}>
              Could not load history.
            </p>
          ) : (
            <ReplacementAuditFlow
              rows={historyData?.rows ?? []}
              groupBySlot={historyTarget?.mode === 'vessel'}
              emptyMessage="No audit entries yet."
            />
          )}
        </ModalContent>
        <ModalFooter>
          <IconBtn title="Close" tone="muted" onClick={() => setHistoryTarget(null)}>
            <XIcon className="h-5 w-5" />
          </IconBtn>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ReplacementPlannerPage;
